import type { DefaultOptionType } from 'antd/lib/select';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import type { CSSProperties, ReactElement } from 'react';

import { Combobox } from '../../combobox/combobox';
import { InputError } from '../input-error';
import { InputLabel } from '../input-label';

import { DropdownPreloader } from './dropdown-preloader';
import styles from './form-select.module.scss';

type Props<V = string | number> = {
  open?: boolean;
  allowClear?: boolean;
  className?: string;
  placeholder?: string;
  searchValue?: string;
  value?: V | null;
  options?: DefaultOptionType[];
  label?: string;
  loading?: boolean;
  showSearch?: boolean;
  filterOption?: (value: string, option?: DefaultOptionType) => boolean;
  errorText?: string | [string, { [locKey: string]: string | number }];
  isError?: boolean;
  isDisabled?: boolean;
  dropdownLoading?: boolean;
  customDropdown?: ReactElement;
  dataTestId?: string;
  dropdownStyle?: CSSProperties;
  onDropdownVisibleChange?: (open: boolean) => void;
  onBlur?: () => void;
  onClear?: () => void;
  onFocus?: () => void;
  onClick?: () => void;
  onSearch?: (value: string) => void;
  onChange?: (value: V) => void;
  dropdownRender?: (origin: ReactElement) => ReactElement;
  getPopupContainer?(props: unknown): HTMLElement;
};

/** Will only be correctly styled if wrapped Form component. */
export const FormSelect = observer(function FormSelect<V>({
  open,
  allowClear = true,
  className,
  placeholder,
  searchValue,
  value,
  label,
  options,
  errorText,
  isError,
  loading,
  showSearch = true,
  isDisabled,
  dropdownLoading,
  customDropdown,
  dropdownStyle,
  dropdownRender: dropdownRenderProp,
  dataTestId,
  onDropdownVisibleChange,
  filterOption,
  onBlur,
  onSearch,
  onFocus,
  onClear,
  onClick,
  onChange,
  getPopupContainer,
}: Props<V>) {
  function dropdownRender(origin: ReactElement): ReactElement {
    if (dropdownLoading) return <DropdownPreloader />;
    if (dropdownRenderProp) return dropdownRenderProp(origin);
    if (customDropdown) return customDropdown;
    return origin;
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      {!!label && <InputLabel text={label} />}
      <Combobox<V>
        open={open}
        value={value}
        data-testid={dataTestId}
        allowClear={allowClear}
        searchValue={searchValue}
        placeholder={placeholder}
        bordered={false}
        className={clsx(styles.select, (!!errorText || isError) && styles.select__error)}
        showSearch={showSearch}
        isDisabled={isDisabled || loading}
        filterOption={filterOption}
        dropdownRender={dropdownRender}
        options={options}
        dropdownStyle={dropdownStyle}
        onDropdownVisibleChange={onDropdownVisibleChange}
        onClick={onClick}
        onClear={onClear}
        onFocus={onFocus}
        onBlur={onBlur}
        onSearch={onSearch}
        onChange={onChange}
        getPopupContainer={getPopupContainer}
      />
      {!!errorText && <InputError errorTextRaw={errorText} />}
    </div>
  );
});
